import { forwardRef, useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import InputError from './InputError';

interface Option {
  value: string;
  label: string;
}

interface LabeledSingleSelectProps {
  label: string;
  error?: string;
  options: Option[];
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: () => void;
}

const LabeledSingleSelect = forwardRef<
  HTMLFieldSetElement,
  LabeledSingleSelectProps
>(
  (
    {
      label,
      error,
      options,
      placeholder = 'Select an option',
      value,
      onChange,
      onBlur,
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);

    const filteredOptions = options.filter((option) =>
      option.label?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedOption = options.find((o) => o.value === value);

    const handleSelect = useCallback(
      (optionValue: string) => {
        onChange?.(optionValue);
        setIsOpen(false);
        setSearchTerm('');
      },
      [onChange]
    );

    const handleClickOutside = useCallback(
      (event: MouseEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          onBlur?.();
        }
      },
      [onBlur]
    );

    const handleEscapeKey = useCallback(
      (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
          onBlur?.();
        }
      },
      [onBlur]
    );

    useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscapeKey);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscapeKey);
      };
    }, [handleClickOutside, handleEscapeKey]);

    return (
      <fieldset className="form-control w-full" ref={ref}>
        <legend className="label">
          <span className="fieldset-legend text-primary font-semibold text-sm">
            {label}
          </span>
        </legend>

        <div className="relative" ref={wrapperRef}>
          <div
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && setIsOpen(true)}
            onClick={() => setIsOpen(true)}
            className={`flex items-center justify-between gap-2 p-2 min-h-[3.5rem] rounded-lg znario:rounded-none border ${error ? 'border-error' : 'border-gray-300'} cursor-text`}
          >
            <div className="flex-1 text-sm px-1 truncate">
              {selectedOption ? (
                selectedOption.label
              ) : (
                <p className="max-w-[10px] text-gray-400">{placeholder}</p>
              )}
            </div>
            <ChevronDown
              className={`h-5 w-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </div>

          {isOpen && (
            <div
              className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg znario:rounded-none shadow-lg max-h-60 overflow-auto"
              role="listbox"
            >
              <div className="p-2">
                <input
                  type="text"
                  className="w-full px-2 py-1 border border-gray-300 rounded znario:rounded-none focus:outline-none text-sm"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Type to search..."
                />
              </div>
              {filteredOptions.length === 0 ? (
                <div className="px-4 py-2 text-sm text-gray-500">
                  No options found
                </div>
              ) : (
                <ul>
                  {filteredOptions.map((option) => (
                    <li
                      key={option.value}
                      className="px-4 py-2 hover:bg-gray-100 cursor-pointer flex items-center justify-between"
                      role="option"
                      aria-selected={value === option.value}
                      onClick={() => handleSelect(option.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSelect(option.value);
                        }
                      }}
                    >
                      {option.label}
                      {value === option.value && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        <InputError error={error} />
      </fieldset>
    );
  }
);

export default LabeledSingleSelect;
